import {
  COOKIE_OAUTH_STATE,
  COOKIE_REFRESH_TOKEN,
  COOKIE_SESSION,
} from '../constants.js';
import type { CookieMutation, CookieMutationOptions, ResolvedConfig } from '../types.js';

function withPartitioned(
  config: ResolvedConfig,
  options: CookieMutationOptions,
): CookieMutationOptions {
  if (!config.cookieOptions.partitioned) return options;
  return { ...options, partitioned: true };
}

export function buildCookieOptions(
  config: ResolvedConfig,
  maxAge: number,
): CookieMutationOptions {
  return withPartitioned(config, {
    httpOnly: true,
    sameSite: config.cookieOptions.sameSite,
    secure: config.cookieOptions.secure,
    path: config.cookieOptions.path,
    maxAge,
  });
}

export function buildSessionCookies(
  config: ResolvedConfig,
  sessionToken: string,
  refreshToken?: string | null,
): CookieMutation[] {
  const cookies: CookieMutation[] = [
    {
      name: COOKIE_SESSION,
      value: sessionToken,
      options: buildCookieOptions(config, config.cookieOptions.sessionMaxAge),
    },
  ];

  if (refreshToken) {
    cookies.push({
      name: COOKIE_REFRESH_TOKEN,
      value: refreshToken,
      options: buildCookieOptions(config, config.cookieOptions.refreshMaxAge),
    });
  } else {
    cookies.push(buildClearCookie(config, COOKIE_REFRESH_TOKEN));
  }

  return cookies;
}

export function buildStateCookie(config: ResolvedConfig, state: string): CookieMutation {
  return {
    name: COOKIE_OAUTH_STATE,
    value: state,
    options: buildCookieOptions(config, config.cookieOptions.stateMaxAge),
  };
}

export function buildClearCookie(config: ResolvedConfig, name: string): CookieMutation {
  return {
    name,
    value: '',
    options: withPartitioned(config, {
      httpOnly: true,
      sameSite: config.cookieOptions.sameSite,
      secure: config.cookieOptions.secure,
      path: config.cookieOptions.path,
      maxAge: 0,
    }),
  };
}

export function buildLogoutCookies(config: ResolvedConfig): CookieMutation[] {
  return [COOKIE_SESSION, COOKIE_REFRESH_TOKEN, COOKIE_OAUTH_STATE].map((name) =>
    buildClearCookie(config, name),
  );
}

export { COOKIE_OAUTH_STATE, COOKIE_REFRESH_TOKEN, COOKIE_SESSION };
